import { useCallback, useEffect, useRef, useState } from "react";
import { getDigitalHumanPageApi } from "../../service/digitalHuman";

const POLL_MS = 5000;

function findScrollParent(el: HTMLElement | null): Element | null {
  let node: HTMLElement | null = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function useDigitalHumanPage(query: {
  scope: DigitalHuman.Scope;
  name?: string;
  trainStatus?: DigitalHuman.TrainStatus | string | number;
  pageSize?: number;
  enabled?: boolean;
  keepPreviousData?: boolean;
}) {
  const pageSize = query.pageSize ?? 20;
  const enabled = query.enabled !== false;
  const name = query.name?.trim() || undefined;
  const trainStatus =
    query.trainStatus === undefined || query.trainStatus === null
      ? undefined
      : String(query.trainStatus).trim() || undefined;

  const [list, setList] = useState<DigitalHuman.Item[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasPending, setHasPending] = useState(false);

  const pageNoRef = useRef(1);
  const listRef = useRef<DigitalHuman.Item[]>([]);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const reqIdRef = useRef(0);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const fetchPage = useCallback(
    async (page: number, mode: "replace" | "append" | "poll") => {
      if (!enabled) return;
      if (mode === "append" && (loadingRef.current || loadingMoreRef.current)) return;

      const reqId = ++reqIdRef.current;
      if (mode === "replace") {
        loadingRef.current = true;
        setLoading(true);
      } else if (mode === "append") {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      try {
        const size =
          mode === "poll"
            ? Math.min(1000, Math.max(pageSize, listRef.current.length || pageSize))
            : pageSize;
        const res = await getDigitalHumanPageApi({
          pageNo: mode === "poll" ? 1 : page,
          pageSize: size,
          scope: query.scope,
          ...(name ? { name } : {}),
          ...(trainStatus ? { trainStatus } : {}),
        });
        if (reqId !== reqIdRef.current) return;

        const next = res.data?.list ?? [];
        setTotal(Number(res.data?.total ?? 0));
        setHasPending(Boolean(res.data?.hasPending));

        if (mode === "append") {
          const merged = [
            ...listRef.current,
            ...next.filter((item) => !listRef.current.some((row) => row.id === item.id)),
          ];
          listRef.current = merged;
          setList(merged);
          pageNoRef.current = page;
        } else {
          listRef.current = next;
          setList(next);
          if (mode === "replace") pageNoRef.current = 1;
        }
      } catch {
        if (reqId !== reqIdRef.current) return;
        if (mode === "replace") {
          listRef.current = [];
          setList([]);
          setTotal(0);
          setHasPending(false);
        }
      } finally {
        if (reqId !== reqIdRef.current) return;
        if (mode === "replace") {
          loadingRef.current = false;
          setLoading(false);
        } else if (mode === "append") {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        }
      }
    },
    [enabled, pageSize, query.scope, name, trainStatus]
  );

  useEffect(() => {
    pageNoRef.current = 1;
    if (!query.keepPreviousData) {
      listRef.current = [];
      setList([]);
      setTotal(0);
    }
    void fetchPage(1, "replace");
  }, [fetchPage, query.keepPreviousData]);

  const hasMore = list.length < total;

  const loadMore = useCallback(() => {
    if (!enabled || !hasMore || loadingRef.current || loadingMoreRef.current) return;
    void fetchPage(pageNoRef.current + 1, "append");
  }, [enabled, hasMore, fetchPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !enabled || !hasMore || loading || loadingMore) return;
    const root = findScrollParent(el);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { root, rootMargin: "240px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, hasMore, loadMore, list.length, loading, loadingMore]);

  useEffect(() => {
    if (!enabled || !hasPending) return;
    const timer = window.setInterval(() => {
      void fetchPage(1, "poll");
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, hasPending, fetchPage]);

  const reload = useCallback(() => {
    listRef.current = [];
    pageNoRef.current = 1;
    setList([]);
    setTotal(0);
    void fetchPage(1, "replace");
  }, [fetchPage]);

  /** 静默刷新：不清空列表，保持列表高度，避免重试后页面跳回顶部 */
  const refresh = useCallback(() => {
    pageNoRef.current = 1;
    void fetchPage(1, "poll");
  }, [fetchPage]);

  return {
    list,
    total,
    loading,
    loadingMore,
    hasMore,
    hasPending,
    loadMore,
    reload,
    refresh,
    sentinelRef,
  };
}
